import { useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { HelpTip } from '../../../../components/HelpTip';
import { Modal } from '../../../../components/Modal';
import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import { TdMoney, TdSeamless } from '../../../../components/inline-add-table/cells';
import { DateField } from '../../../../components/inputs/DateField';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { uid } from '../../../../lib/uid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { salaryGradesApi } from '../plantillaApi';
import { SALARY_ACTIVATION, SALARY_TYPE, type SalaryActivationValue, type SalaryTypeValue } from '../plantillaTypes';
import { SALARY_GRADES, SALARY_STEPS, sampleRatesFor, sampleTranche } from '../utils/sampleSalaryGrades';

const ID_PREFIX = 'salary-grades';

/** `salary-grades-items.4.step-rates.2` — the id the server's `items.4.stepRates.2` resolves to. */
const rateCellId = (index: number, step: number) => `${ID_PREFIX}-items.${index}.step-rates.${step}`;

/** The rows the grid opens on (product call, 16 Oct 2026) — what a tranche usually needs. */
const DEFAULT_ROWS = 30;

const STEPS = Array.from({ length: SALARY_STEPS }, (_, step) => step);

const RATES_HELP =
  'One row per grade, with its monthly rate at each step. Every rate must be higher than the ' +
  'step before it and than the grade above it at the same step. Rows left empty are not saved.';

interface DraftRow {
  key: string;
  /** Seeded and LOCKED — the reader never types it, so two rows can never share one. */
  grade: number;
  /** By position: index 0 is step 1. */
  rates: (number | null)[];
}

const NO_GRADES: readonly number[] = [];

const rowFor = (grade: number): DraftRow => ({ key: uid(), grade, rates: STEPS.map(() => null) });

/**
 * The grade the next row is given — the LOWEST one not already on the form, so a removed row's
 * grade is the first offered back, and the grid never holds a repeat.
 */
function nextGrade(rows: DraftRow[], held: readonly number[]): number | null {
  const used = new Set([...held, ...rows.map((row) => row.grade)]);

  for (let grade = 1; grade <= SALARY_GRADES; grade++) {
    if (!used.has(grade)) return grade;
  }

  return null;
}

/** Nothing typed — the shape the server's `IsBlankSalaryGradeLine` tests. The grade is seeded, so it is not consulted. */
function isBlank(row: DraftRow): boolean {
  return row.rates.every((rate) => rate === null);
}

/** A row that can be saved: at least one rate. No ceiling on the amount — the server's cap is refused there and names the cell. */
function isFilled(row: DraftRow): boolean {
  return row.rates.some((rate) => rate !== null && rate >= 0);
}

/** `"3:1"` — row 3, step 2. */
const cellKey = (index: number, step: number) => `${index}:${step}`;

/**
 * Why each cell is wrong — the server's `SalaryScheduleOrder`, mirrored so the cell says so
 * before the reader submits. ONE RULE WRITTEN TWICE: the server also checks what the tranche
 * already holds, which this form cannot see.
 *
 * A rate must be higher than the nearest filled step to its LEFT and than the nearest filled
 * grade ABOVE it at the same step — strictly. Blank cells are skipped, never read as zero.
 * Grades are locked and distinct, so each row is one grade and the rows sort by it.
 */
function findProblems(rows: DraftRow[]): Map<string, string> {
  const problems = new Map<string, string>();

  const graded = rows.map((row, index) => [row.grade, index] as const).sort(([a], [b]) => a - b);

  // Along each grade's steps.
  for (const [grade, index] of graded) {
    let previous: number | null = null;
    let previousStep = -1;

    rows[index].rates.forEach((rate, step) => {
      if (rate === null) return;

      if (previous !== null && rate <= previous) {
        problems.set(cellKey(index, step), `SG ${grade} step ${step + 1} must be higher than step ${previousStep + 1}.`);
      }

      previous = rate;
      previousStep = step;
    });
  }

  // Down the grades at each step.
  for (const step of STEPS) {
    let previous: { grade: number; rate: number } | null = null;

    for (const [grade, index] of graded) {
      const rate = rows[index].rates[step];
      if (rate === null) continue;

      if (previous !== null && rate <= previous.rate && !problems.has(cellKey(index, step))) {
        problems.set(
          cellKey(index, step),
          `SG ${grade} step ${step + 1} must be higher than SG ${previous.grade} at the same step.`,
        );
      }

      previous = { grade, rate };
    }
  }

  return problems;
}

/**
 * Declare a tranche's rates in one pass — the type, tranche and effectivity date once, then a
 * row per grade with a monthly rate for each of its eight steps.
 *
 * Product calls, 16 Oct 2026: first a one-rate form became a grid of grade, step, date and rate;
 * then the step became the COLUMNS and the date moved to the header, because a published
 * schedule is a grade-by-step table that takes effect on one day. The grid opens on thirty rows;
 * the ones left empty are dropped by the server, and `+` adds more.
 */
export function SalaryGradeTrancheModal({
  open,
  into,
  onClose,
  onSaved,
}: {
  open: boolean;
  /**
   * Adds grades to an EXISTING tranche, from its profile: the type, tranche and date are the
   * tranche's and locked, and the grid offers only the grades it does not hold yet. Absent, the
   * form declares a new tranche.
   */
  into?: {
    type: SalaryTypeValue;
    tranche: number;
    effectivityDate: string;
    activationStatus: SalaryActivationValue;
    heldGrades: readonly number[];
  };
  onClose: () => void;
  onSaved: () => void;
}) {
  const held = into?.heldGrades ?? NO_GRADES;
  const locked = into !== undefined;

  const [salaryType, setSalaryType] = useState<string | null>(String(into?.type ?? SALARY_TYPE.general));
  const [tranche, setTranche] = useState(String(into?.tranche ?? 1));
  // Autogenerate fills the tranche only while it is still the seeded default.
  const [trancheTouched, setTrancheTouched] = useState(locked);
  const [effectivityDate, setEffectivityDate] = useState(into?.effectivityDate.slice(0, 10) ?? '');
  // Seeded EMPTY on a new tranche: either answer is a claim, and the server refuses none.
  const [activationStatus, setActivationStatus] = useState<string | null>(into ? String(into.activationStatus) : null);
  /*
    SG 1 to 30 on a new tranche (product call, 16 Oct 2026) — the grade is the row's, never
    typed. Adding to a tranche, ONE row (same day: "only autoadd 1 row") — the first grade it
    does not already hold; `+` offers the next.
  */
  const [rows, setRows] = useState<DraftRow[]>(() => {
    const seeded: DraftRow[] = [];
    const count = locked ? 1 : DEFAULT_ROWS;

    for (let grade = 1; grade <= SALARY_GRADES && seeded.length < count; grade++) {
      if (!held.includes(grade)) seeded.push(rowFor(grade));
    }

    return seeded;
  });

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const toast = useToast();

  const trancheNumber = Number(tranche);
  const trancheValid = Number.isInteger(trancheNumber) && trancheNumber >= 1 && trancheNumber <= 20;

  const started = useMemo(() => rows.filter((row) => !isBlank(row)), [rows]);
  const filled = useMemo(() => rows.filter(isFilled), [rows]);
  const problems = useMemo(() => findProblems(rows), [rows]);

  const valid =
    salaryType !== null &&
    trancheValid &&
    effectivityDate.length > 0 &&
    activationStatus !== null &&
    filled.length > 0 &&
    filled.length === started.length &&
    problems.size === 0;

  const canSave = valid && confirmed && !saving;
  const incomplete = filled.length < started.length;
  const suggested = nextGrade(rows, held);

  /** Adds the suggested grade in GRADE order, so the grid reads as the schedule does. */
  function addRow() {
    setRows((current) => {
      const grade = nextGrade(current, held);
      if (grade === null) return current;

      return [...current, rowFor(grade)].sort((a, b) => a.grade - b.grade);
    });
  }

  function patchRate(key: string, step: number, rate: number | null) {
    setRows((current) =>
      current.map((row) =>
        row.key === key ? { ...row, rates: row.rates.map((r, s) => (s === step ? rate : r)) } : row,
      ),
    );
  }

  async function save() {
    if (!canSave || salaryType === null) return;

    setSaving(true);
    setError(null);

    try {
      /*
        EVERY row is sent, blanks included, so the index a refusal names is the index of the row
        ON SCREEN. Dropping them here would shift every later row's number and point the reader
        at the wrong cell; the server drops them after validating.
      */
      const written = await salaryGradesApi.create({
        type: Number(salaryType) as SalaryTypeValue,
        tranche: trancheNumber,
        effectivityDate,
        activationStatus: activationStatus === null ? null : (Number(activationStatus) as SalaryActivationValue),
        items: rows.map((row) => ({ grade: row.grade, stepRates: row.rates })),
      });

      toast.success(`${written.length} rate${written.length === 1 ? '' : 's'} saved to tranche ${trancheNumber}.`);
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the salary grades.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID_PREFIX, fieldIds: { items: `${ID_PREFIX}-add-row` } });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={locked ? 'Add salary grades' : 'New salary grade'}
      /* `3xl`: the grade and eight step columns with both gutters are 1,048px. */
      size="3xl"
      id="salary-grades-modal"
      /*
        Prices every row the reader has NOT started, at its own locked grade, with rates that
        honour the ordering rule. Rows already typed in are left alone, and so are a tranche and
        a date the reader chose.
      */
      autogenerate={{
        onGenerate: () => {
          if (!trancheTouched) setTranche(sampleTranche());
          if (effectivityDate.length === 0) setEffectivityDate(todayCalendarDate());
          // A sample tranche is a DRAFT: test data is never put in force for other modules.
          if (activationStatus === null) setActivationStatus(String(SALARY_ACTIVATION.draft));

          setRows((current) =>
            current.map((row) => (isBlank(row) ? { ...row, rates: sampleRatesFor(row.grade) } : row)),
          );
        },
        disabled: saving,
      }}
      confirm={{
        id: 'salary-grades-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="salary-grades-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="salary-grades-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          FOUR ON ONE ROW, against the form grid's two (product calls: the date "alongside the
          type and tranche", 16 Oct 2026; the activation status, 19 Oct 2026). The four describe the
          tranche as one thing and are read together.
        */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <Select
            id="salary-grades-type"
            label="Type"
            required
            clearable={false}
            description="Which salary type these rates belong to. The same grade pays differently on each."
            value={salaryType}
            onChange={setSalaryType}
            options={[
              { value: String(SALARY_TYPE.general), label: 'General' },
              { value: String(SALARY_TYPE.health), label: 'Health' },
            ]}
            disabled={saving || locked}
          />

          <TextField
            id="salary-grades-tranche"
            label="Tranche"
            required
            type="number"
            min={1}
            max={20}
            description="Which tranche these rates belong to. The same grade and step is a different amount in each."
            value={tranche}
            onChange={(e) => {
              setTranche(e.target.value);
              setTrancheTouched(true);
            }}
            error={trancheValid ? undefined : 'Enter a tranche between 1 and 20.'}
            disabled={saving || locked}
          />

          {/* Locked when adding: the grades join a tranche that already took effect on its date. */}
          <DateField
            id="salary-grades-effectivity-date"
            label="Effective from"
            required
            description="The day this tranche takes effect. Every rate below shares it."
            value={effectivityDate}
            onChange={setEffectivityDate}
            disabled={saving || locked}
          />

          {/* Locked when adding: the grades take the tranche's own status. */}
          <Select
            id="salary-grades-activation-status"
            label="Activation status"
            required
            clearable={false}
            description="Draft keeps the tranche to Salary Grades — no position, post, vacancy or posting reads its rates. Active puts them in force."
            value={activationStatus}
            onChange={setActivationStatus}
            // The status's own order — Draft, then Active — not the alphabet's.
            sort="none"
            options={[
              { value: String(SALARY_ACTIVATION.draft), label: 'Draft' },
              { value: String(SALARY_ACTIVATION.active), label: 'Active' },
            ]}
            disabled={saving || locked}
          />
        </div>

        <div>
          <div className="flex min-h-5 items-center gap-1.5 pb-1.5">
            <p className="text-sm text-slate-600">Salary grades</p>
            <HelpTip label={RATES_HELP} />
          </div>

          <p className="sr-only">{RATES_HELP}</p>

          <InlineAddTable
            headers={[
              // No asterisk: the grade is supplied by the form, not the reader.
              { label: 'Grade', align: 'right' },
              ...STEPS.map((step) => ({ label: `Step ${step + 1}`, align: 'right' as const })),
            ]}
            colWidths={[80, ...STEPS.map(() => 110)]}
            /* v2: the column set changed from Grade · Step · Effective from · Monthly rate. */
            resizeKey="salary-grade-tranche.colWidths.v2"
            addButtonId="salary-grades-add-row"
            addDisabled={saving || incomplete || suggested === null}
            addDisabledReason={
              incomplete
                ? 'Finish the row you have started before adding another.'
                : `Every grade from 1 to ${SALARY_GRADES} is already on the form.`
            }
            rowCount={rows.length}
            onAddRow={addRow}
          >
            {rows.map((row, index) => (
              <InlineRow
                key={row.key}
                idx={index + 1}
                onRemove={() => setRows((current) => current.filter((r) => r.key !== row.key))}
              >
                {/* LOCKED: read-only, the form's own value — the AddSlots item-number cell. */}
                <TdSeamless align="right">
                  <span className="font-mono text-sm tabular-nums text-slate-600">{row.grade}</span>
                </TdSeamless>

                {STEPS.map((step) => (
                  <TdMoney
                    key={step}
                    id={rateCellId(index, step)}
                    ariaLabel={`Step ${step + 1} monthly rate, row ${index + 1}`}
                    value={row.rates[step]}
                    invalid={problems.get(cellKey(index, step)) ?? null}
                    onChange={(next) => patchRate(row.key, step, next)}
                    disabled={saving}
                  />
                ))}
              </InlineRow>
            ))}
          </InlineAddTable>
        </div>
      </div>
    </Modal>
  );
}
